// webapp/src/SignupPage.js
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import GuestAccountUpgrade from './GuestAccountUpgrade';
import EmailConfirmationPanel from './EmailConfirmationPanel';
import { authErrorParams } from './authErrors';
import { hasGuestEmailSetup } from './guestUpgrade';
import { supabase } from './supabase';
import { signInWithOAuth } from './oauth';
import config_names from './config_names';
import logo from './logo.svg';
import { 
  FiMail, 
  FiAlertCircle, 
  FiArrowRight, 
  FiArrowLeft,
  FiCheckCircle
} from 'react-icons/fi';
import { FaGoogle } from 'react-icons/fa';
import LanguageSelector from './LanguageSelector';
import { useI18n } from './i18n';
import {
  buildProfileAttribution,
  trackAuthEvent,
  trackAuthPageView,
} from './authAttribution';
import { getAuthRedirectPath, getAuthRedirectUrl } from './authRedirect';
import { browserSignupAttribution, isVerifiedRegistration } from './signupAttribution';
import './auth-pages.css';
import { getBillingPriceSummary } from './pricing';

function SignupPage() {
  const { currentUser } = useAuth();
  const isGuest = Boolean(currentUser?.is_anonymous);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [showEmailSignup, setShowEmailSignup] = useState(() => new URLSearchParams(window.location.search).get('auth_method') === 'email');
  const [isSuccess, setIsSuccess] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const autoGoogleSignupStartedRef = useRef(false);
  const authRedirectPath = getAuthRedirectPath(location.state);
  const premiumInterval = new URL(authRedirectPath, window.location.origin).searchParams.get('premium');
  const premiumIntent = ['monthly', 'annual'].includes(premiumInterval);
  const premiumPrice = getBillingPriceSummary(premiumInterval);
  const finishGuestEmail = new URLSearchParams(location.search).has('finish_guest_email') || hasGuestEmailSetup(currentUser?.id);

  useEffect(() => {
    const confirmedPending = pendingEmail && currentUser?.email?.toLowerCase() === pendingEmail.toLowerCase();
    if ((premiumIntent || confirmedPending) && isVerifiedRegistration(currentUser) && !finishGuestEmail) {
      navigate(authRedirectPath, { replace: true });
    }
  }, [authRedirectPath, currentUser, navigate, premiumIntent, finishGuestEmail, pendingEmail]);

  const getErrorMessage = useCallback((error) => {
    if (!error) return t('auth.unexpected');

    switch (error.message) {
      case 'User already registered':
        return t('auth.userExists');
      case 'Password should be at least 6 characters':
        return t('auth.weakPassword');
      default:
        return error.message || t('auth.unexpected');
    }
  }, [t]);

  const handleGoogleSignup = useCallback(async () => {
    try {
      trackAuthEvent('signup_attempt', { method: 'google' });
      const { error } = await signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: getAuthRedirectUrl(location.state, {
            auth_method: 'google',
            auth_target: 'signup',
          })
        }
      });
  
      if (error) throw error;
      // The redirect will be handled by Google OAuth
    } catch (error) {
      trackAuthEvent('signup_error', { method: 'google', ...authErrorParams(error, 'oauth_start') });
      setError(getErrorMessage(error));
    }
  }, [getErrorMessage, location.state]);

  useEffect(() => {
    trackAuthPageView('signup');
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (isGuest || finishGuestEmail || (premiumIntent && currentUser)) return;
    if (params.get('auto_oauth') !== 'google' || autoGoogleSignupStartedRef.current) return;
    autoGoogleSignupStartedRef.current = true;
    handleGoogleSignup();
  }, [handleGoogleSignup, location.search, isGuest, premiumIntent, currentUser, finishGuestEmail]);

  const handleSkip = () => {
    trackAuthEvent('signup_skip');
    navigate('/dashboard');
  };

  const isBlockedDomain = (email) => {
    const blockedDomains = ['euleina.com', 'fursee.com', 'gardsiir.com'];
    const domain = email.toLowerCase().split('@')[1];
    return blockedDomains.includes(domain);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    trackAuthEvent('signup_attempt', { method: 'email' });
  
    try {
      // Check if email domain is blocked
      if (isBlockedDomain(email)) {
        // Show fake success message for blocked domains
        setIsSuccess(true);
        trackAuthEvent('signup_blocked_domain');
        return;
      }

      const profileAttribution = buildProfileAttribution({
        method: 'email',
        authTarget: 'signup',
        source: 'webapp',
      });

      // Simple sign-up without any options
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: getAuthRedirectUrl(location.state, { auth_method: 'email', auth_target: 'signup' }),
          data: {
            signup_source: 'webapp',
            ...profileAttribution,
            signup_attribution: browserSignupAttribution(),
          },
        },
      });
  
      if (error) throw error;

      // AuthContext persists the profile and records a verified registration.
      // An obfuscated duplicate signup or pending email is not a conversion.
      setIsSuccess(true);
      if (data?.session && isVerifiedRegistration(data.session.user)) {
        navigate(authRedirectPath, { replace: true });
      } else {
        setPendingEmail(email.trim());
        trackAuthEvent('signup_confirmation_pending', { method: 'email' });
      }
    } catch (error) {
      trackAuthEvent('signup_error', { method: 'email', ...authErrorParams(error, 'email_signup') });
      setError(error.message || t('auth.unexpected'));
    } finally { setSubmitting(false); }
  };

  if (isGuest || finishGuestEmail) {
    return <GuestAccountUpgrade autoProvider={new URLSearchParams(location.search).get("auto_oauth")} finishEmail={finishGuestEmail} />;
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-topline">
          <div className="auth-brand">
            <img src={logo} alt="" />
            <span>{config_names.projectName}</span>
          </div>
          <LanguageSelector />
        </div>
        <div className="auth-header">
          <h1>{t('auth.createAccount')}</h1>
          <p>{t('auth.signupSubtitle', { project: config_names.projectName })}</p>
        </div>

        {premiumIntent && <p className="auth-premium-hint">
          {t('billing.signupPremiumHint')}<br />
          <strong>{t(premiumInterval === 'annual' ? 'billing.annual' : 'billing.monthly')} · {premiumPrice.monthlyDisplayPrice}{t('billing.perMonth')}</strong><br />
          {t(premiumInterval === 'annual' ? 'billing.billedAnnual' : 'billing.billedMonthly', { annualTotal: premiumPrice.annualTotalPrice })}
        </p>}

        {error && (
          <div className="auth-error">
            <FiAlertCircle />
            <span>{error}</span>
          </div>
        )}

        {pendingEmail && <EmailConfirmationPanel email={pendingEmail} redirectPath={authRedirectPath}
          redirectUrl={getAuthRedirectUrl(location.state, { auth_method: 'email', auth_target: 'signup' })}
          onEdit={() => { setPendingEmail(''); setIsSuccess(false); setPassword(''); }} />}

        {isSuccess && !pendingEmail && (
          <div className="auth-success">
            <FiCheckCircle />
            <span>{t('auth.signupSuccess')}</span>
          </div>
        )}

        {!showEmailSignup && !isSuccess ? (
          <>
            {config_names.allow_skip_login_for_now && (
              <button 
                className="auth-btn secondary-btn" 
                onClick={handleSkip}
              >
                <FiArrowRight /> {t('auth.skip')}
              </button>
            )}
            
            <button 
              className="auth-btn google-btn" 
              onClick={handleGoogleSignup}
            >
              <FaGoogle /> {t('auth.signUpGoogle')}
            </button>
            
            <div className="auth-divider">{t('auth.or')}</div>
            
            <button 
              className="auth-btn primary-btn" 
              onClick={() => {
                trackAuthEvent('signup_email_form_open');
                setShowEmailSignup(true);
              }}
            >
              <FiMail /> {t('auth.signUpEmail')}
            </button>
          </>
        ) : !isSuccess ? (
          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <div className="input-icon-wrapper">
                <input 
                  type="email" 
                  className="form-control" 
                  placeholder={t('common.email')}
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  required 
                />
              </div>
            </div>
            
            <div className="form-group">
              <div className="input-icon-wrapper">
                <input 
                  type="password" 
                  className="form-control" 
                  placeholder={t('auth.passwordMin')}
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  required 
                />
              </div>
            </div>
            
            <button type="submit" className="auth-btn primary-btn" disabled={submitting}>
              <FiArrowRight /> {t('auth.createAccount')}
            </button>
            
            <button 
              type="button" 
              className="auth-btn secondary-btn" 
              onClick={() => {
                trackAuthEvent('signup_email_form_close');
                setShowEmailSignup(false);
              }}
            >
              <FiArrowLeft /> {t('common.back')}
            </button>
          </form>
        ) : null}

        {!isSuccess && (
          <div className="auth-footer">
            <span>{t('auth.alreadyAccount')} </span>
            <Link to="/login" state={{ from: authRedirectPath }}>{t('common.signIn')}</Link>
          </div>
        )}

        <div className="auth-legal">
          {t('auth.legal', { project: config_names.projectName_genitive })}{' '}
          <a target="_top" href={`${config_names.domain}/terms`}>{t('auth.terms')}</a> {t('auth.and')}{' '}
          <a target="_top" href={`${config_names.domain}/privacy`}>{t('auth.privacy')}</a>.
        </div>
      </div>
    </div>
  );
}

export default SignupPage;
